<template>
  <div>
    <v-card max-height="700">
      <v-card-title class="cardheader">
        <span class="headline black--text">Data generation redirection</span>
        <exo-help-dialog
          :color="'black--text'"
          margin="m-0"
          title="Data generation redirection"
        >
          You can use the results from this maneuver analysis to
          <strong>generate new data</strong> for the observed space object. You
          will be redirected to the creation form of the data generation mission
          with the following fields already filled out:
          <br />
          <br />
          <ul>
            <li>
              The <em>space object</em> will be the one used for this maneuver
              analysis.
            </li>
            <li>
              The <em>initial orbit</em> will be the last known orbit for this
              space object during the maneuver analysis.
            </li>
          </ul>
        </exo-help-dialog>
        <v-spacer></v-spacer>
        <v-btn icon light class="m-0" v-on:click="closeDialog()">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div class="dialogSectionClass" v-if="globalErrorMessage != null">
          <v-col
            ><v-row class="dialogueinfoline error--text">{{
              globalErrorMessage
            }}</v-row></v-col
          ><v-divider />
        </div>
        <div
          class="dialogSectionClass"
          v-if="propellantUsedDefined && usedPropellant !== 0"
        >
          <v-col>
            <v-row>
              <span class="dialogueinfoline"
                >The space object has used an estimated
                <strong>{{ propellantDisplayString }}</strong> of propellant in
                the observed period. Do you want to take this change into
                account for the data generation?</span
              >
            </v-row>
            <v-row class="dialogueoption">
              <input
                class="dialogueradiobutton"
                type="radio"
                id="updatePropellantMass"
                :value="true"
                v-model="updatePropellantMass"
              />
              <label for="updatePropellantMass"
                >Use a copy of the space object with an
                <strong>updated propellant mass</strong>
                (this will create a new object in the database).
              </label>
            </v-row>
            <v-row class="dialogueoption">
              <input
                class="dialogueradiobutton"
                type="radio"
                id="notUpdatePropellantMass"
                :value="false"
                v-model="updatePropellantMass"
              />
              <label for="notUpdatePropellantMass"
                >Use the original space object, with
                <strong>no modification</strong> of the propellant mass.
              </label>
            </v-row>
            <v-row v-if="updatePropellantMass === true">
              <v-text-field
                label="Space Object copy name"
                v-model="newSpaceObjectName"
                maxlength="100"
                :rules="[spaceObjectNameFeedback]"
                :error-messages="spaceObjectNameErrorMessage"
              ></v-text-field
            ></v-row>
          </v-col>
          <v-divider />
        </div>
        <template v-if="!updatePropellantMass">
          <div class="dialogSectionClass" v-if="spaceObjectModified">
            <v-col>
              <v-row>
                <span class="dialogueinfoline warning--text"
                  >Warning: The original space object used for this maneuver
                  analysis has been <strong>modified</strong> since the results
                  were computed.</span
                >
              </v-row>
              <v-row class="dialogueoption">
                <input
                  class="dialogueradiobutton"
                  type="radio"
                  id="notCopyIfModified"
                  :value="false"
                  v-model="copySpaceObjectWhenModified"
                />
                <label for="notCopyIfModified"
                  >Use the <strong>modified version</strong> of the space object
                  as input for the data generation</label
                >
              </v-row>
              <v-row class="dialogueoption">
                <input
                  class="dialogueradiobutton"
                  type="radio"
                  id="copyIfModified"
                  :value="true"
                  v-model="copySpaceObjectWhenModified"
                />
                <label for="copyIfModified"
                  >Use the <strong>original space</strong> object as input for
                  the data generation (a copy of the original space object will
                  be created in the database)</label
                >
              </v-row>
              <v-row v-if="copySpaceObjectWhenModified === true">
                <v-text-field
                  label="Space Object copy name"
                  v-model="newSpaceObjectName"
                  maxlength="100"
                  :rules="[spaceObjectNameFeedback]"
                  :error-messages="spaceObjectNameErrorMessage"
                ></v-text-field
              ></v-row>
            </v-col>
            <v-divider />
          </div>
          <div class="dialogSectionClass" v-if="spaceObjectDeleted">
            <v-col>
              <v-row>
                <span class="dialogueinfoline warning--text"
                  >Warning: The original space object used for this maneuver
                  analysis has been <strong>deleted</strong> since the results
                  were computed.</span
                >
              </v-row>
              <v-row class="dialogueoption">
                <input
                  class="dialogueradiobutton"
                  type="radio"
                  id="notCopyIfDeleted"
                  :value="false"
                  v-model="copySpaceObjectWhenDeleted"
                />
                <label for="notCopyIfDeleted"
                  ><strong>Do not</strong> automatically fill the space object
                  field</label
                >
              </v-row>
              <v-row class="dialogueoption">
                <input
                  class="dialogueradiobutton"
                  type="radio"
                  id="copyIfDeleted"
                  :value="true"
                  v-model="copySpaceObjectWhenDeleted"
                />
                <label for="copyIfDeleted"
                  >Use the <strong>original space</strong> object as input for
                  the data generation (a copy of the original space object will
                  be created in the database)</label
                >
              </v-row>
              <v-row v-if="copySpaceObjectWhenDeleted === true">
                <v-text-field
                  label="Space Object copy name"
                  v-model="newSpaceObjectName"
                  maxlength="100"
                  :rules="[spaceObjectNameFeedback]"
                  :error-messages="spaceObjectNameErrorMessage"
                ></v-text-field
              ></v-row>
            </v-col>
            <v-divider />
          </div>
        </template>
        <div class="dialogSectionClass">
          <v-col
            ><v-row>
              <span class="orbitmessage"
                >A copy of the last known orbit for this mission will be created
                in the database. Enter a name for this orbit:</span
              >
            </v-row>
            <v-row>
              <v-text-field
                label="Orbit name"
                color="primary"
                v-model="newOrbitName"
                maxlength="100"
                :rules="[orbitNameFeedback]"
                :error-messages="orbitNameErrorMessage"
              ></v-text-field>
            </v-row>
            <v-row justify="end">
              <v-btn
                :disabled="!isInputsValid || redirecting"
                color="primary"
                @click="toDataGenerator()"
                >Proceed to Data Generator</v-btn
              >
            </v-row>
          </v-col>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from '../../services/axios'

import Vue from 'vue'
import VueAxios from 'vue-axios'
import errorMixin from '../../mixins/logHandler.js'
import dashboardTools from '../../tools/dashboard'
import dataTools from '../../tools/data.js'
Vue.use(VueAxios, axios)

export default {
  name: 'ManeuverAnalysisToDataGenerationDialogue',

  mixins: [errorMixin],

  props: {
    isOpenDialog: Boolean,
    orbit: {
      Object
    },
    spaceObject: {
      Object
    },
    usedPropellant: {
      Number
    },
    remainingPropellant: {
      Number
    },
    propellantFieldType: {
      String
    }
  },

  mounted() {},

  watch: {
    isOpenDialog: {
      handler(newValue) {
        if (newValue === true) {
          this.updateSpaceObjectFromDb()
        }
      },
      immediate: true
    }
  },

  data() {
    return {
      redirecting: false,
      spaceObjectFromDb: null,
      newOrbitName: '',
      newSpaceObjectName: '',
      copySpaceObjectWhenModified: false,
      copySpaceObjectWhenDeleted: false,
      updatePropellantMass: true,
      globalErrorMessage: null,
      spaceObjectNameErrorMessage: null,
      orbitNameErrorMessage: null
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser'
    }),
    spaceObjectDeleted() {
      return this.spaceObjectFromDb == null
    },
    spaceObjectModified() {
      return (
        this.spaceObjectDeleted === false &&
        this.spaceObjectFromDb?.updatedAt !== this.spaceObject?.updatedAt
      )
    },
    isInputsValid() {
      if (this.spaceObjectDeleted) {
        return (
          this.orbitNameState &&
          (!this.copySpaceObjectWhenDeleted || this.spaceObjectNameState)
        )
      } else if (this.spaceObjectModified) {
        return (
          this.orbitNameState &&
          (!this.copySpaceObjectWhenModified || this.spaceObjectNameState)
        )
      } else {
        return this.orbitNameState
      }
    },
    orbitNameFeedback() {
      let feedback = true
      if (this.orbitNameState !== null && !this.orbitNameState) {
        feedback =
          "Please only use the allowed characters : a-z A-Z 0-9 ' ',.-+=/_#@$&:%|()]["
      }
      return feedback
    },
    orbitNameState() {
      let valid = null
      if (this.newOrbitName !== '') {
        let allowedChars = RegExp(
          '^[a-zA-Z0-9 ,\\.\\-\\+=\\/_@#\\$&:%\\|\\(\\)\\[\\]]+$'
        )
        valid = allowedChars.test(this.newOrbitName)
      }
      return valid
    },
    spaceObjectNameFeedback() {
      let feedback = true
      if (this.spaceObjectNameState !== null && !this.spaceObjectNameState) {
        feedback =
          "Please only use the allowed characters : a-z A-Z 0-9 ' ',.-+=/_#@$&:%|()]["
      }
      return feedback
    },
    spaceObjectNameState() {
      let valid = null
      if (this.newSpaceObjectName !== '') {
        let allowedChars = RegExp(
          '^[a-zA-Z0-9 ,\\.\\-\\+=\\/_@#\\$&:%\\|\\(\\)\\[\\]]+$'
        )
        valid = allowedChars.test(this.newSpaceObjectName)
      }
      return valid
    },
    propellantUsedDefined() {
      return (
        this.usedPropellant != null &&
        this.remainingPropellant != null &&
        this.propellantFieldType != null
      )
    },
    propellantDisplayString() {
      const value = dataTools.convertSi2Display(
        this.propellantFieldType,
        this.usedPropellant
      )
      const unit = dataTools.getDefaultGuiUnit(this.propellantFieldType)

      return unit !== '' ? `${value} ${unit}` : value
    }
  },

  methods: {
    closeDialog() {
      this.$emit('update:isOpenDialog', false)
    },

    clearAllNameErrorMessages() {
      this.orbitNameErrorMessage = null
      this.spaceObjectNameErrorMessage = null
    },

    async getSpaceObjectFromDb() {
      return this.$http
        .get(
          '/users/' +
            this.currentUser.id +
            '/spaceobjectdefinitions/' +
            this.spaceObject.id
        )
        .then((response) => response.data)
        .catch((error) => {
          if (error?.response?.status === 404) {
            return null
          } else {
            this.globalErrorMessage = this.getErrorMessage(error)
            this.displayMessage(
              'Error while retrieving spacecraft from database',
              error,
              'notification',
              'error'
            )
            return this.spaceObject
          }
        })
    },

    async updateSpaceObjectFromDb() {
      this.globalErrorMessage = null
      this.spaceObjectFromDb = await this.getSpaceObjectFromDb()
    },

    spaceObjectCopyErrorHandler(error) {
      if (error.response && error.response.status === 409) {
        this.spaceObjectNameErrorMessage = `A space object with the name ${this.newSpaceObjectName} already exists in the database.`
      } else {
        this.displayMessage(
          'Error while saving new copy of space object in database',
          error,
          'notification',
          'error'
        )
      }
      return null
    },

    async saveSpaceObject() {
      let spaceObject = this.spaceObject
      if (
        this.propellantUsedDefined &&
        this.usedPropellant !== 0 &&
        this.updatePropellantMass
      ) {
        const modifiedSpaceObject = {
          ...this.spaceObject,
          propellantMass: this.spaceObject.propellantMass - this.usedPropellant,
          totalMass: this.spaceObject.totalMass - this.usedPropellant
        }
        spaceObject = await this.copySpaceObjectToDb(modifiedSpaceObject)
          .then((response) => response.data)
          .catch((error) => this.spaceObjectCopyErrorHandler(error))
      } else if (this.spaceObjectDeleted) {
        if (this.copySpaceObjectWhenDeleted) {
          spaceObject = await this.copySpaceObjectToDb(this.spaceObject)
            .then((response) => response.data)
            .catch((error) => this.spaceObjectCopyErrorHandler(error))
        } else {
          spaceObject = null
        }
      } else if (this.spaceObjectModified) {
        if (this.copySpaceObjectWhenModified) {
          spaceObject = await this.copySpaceObjectToDb(this.spaceObject)
            .then((response) => response.data)
            .catch((error) => this.spaceObjectCopyErrorHandler(error))
        } else {
          spaceObject = this.spaceObjectFromDb
        }
      }
      return spaceObject
    },
    copySpaceObjectToDb(spaceObject) {
      return this.$http.post(
        '/users/' + this.currentUser.id + '/spaceobjectdefinitions',
        {
          ...spaceObject,
          id: undefined,
          name: this.newSpaceObjectName,
          displayedName: this.newSpaceObjectName
        }
      )
    },

    async saveOrbit() {
      return this.$http
        .post('/users/' + this.currentUser.id + '/orbitdefinitions', {
          ...this.orbit,
          name: this.newOrbitName,
          definitionType: 'EllipticalWithPerigeeEcc',
          altitude: 'NaN'
        })
        .then((response) => response.data)
        .catch((error) => {
          if (error.response && error.response.status === 409) {
            this.orbitNameErrorMessage = `An orbit with the name ${this.newOrbitName} already exists in the database.`
          } else {
            this.displayMessage(
              'Error while saving orbit in database',
              error,
              'notification',
              'error'
            )
            return null
          }
        })
    },

    async requestDataGeneratorMission(orbit, spaceObject) {
      const mission = {
        spaceObject,
        initialOrbit: orbit,
        typeName: 'DataGeneratorRequest'
      }
      this.$router.push({ path: '/datageneratorrequest' })
      this.sendMissionEvent('create-mission', mission)
    },

    sendMissionEvent(eventName, mission) {
      // event send to root because it should be treated by sub components
      // use nextTick to be sure mission components are created and can handle events
      Vue.nextTick(() => {
        this.$root.$emit(eventName, mission)
      })
    },

    async toDataGenerator() {
      this.redirecting = true
      this.clearAllNameErrorMessages()
      const orbit = await this.saveOrbit()
      if (orbit != null) {
        const spaceObject = await this.saveSpaceObject()
        if (
          spaceObject != null ||
          (!this.updatePropellantMass &&
            this.spaceObjectDeleted &&
            !this.copySpaceObjectWhenDeleted)
        ) {
          this.requestDataGeneratorMission(orbit, spaceObject)
        } else {
          this.deleteOrbitFromDatabase(orbit)
        }
      }
      this.redirecting = false
    },

    async deleteOrbitFromDatabase(orbit) {
      return this.$http
        .delete(
          '/users/' + this.currentUser.id + '/orbitdefinitions/' + orbit.id
        )
        .catch((error) =>
          this.displayMessage(
            'Error while cleaning up database.',
            error,
            'notification',
            'error'
          )
        )
    }
  }
}
</script>

<style scoped>
.cardheader {
  background: #3be287;
}
.dialogSectionClass {
  margin-top: 4px;
}
.dialogueinfoline {
  font-size: 130%;
  margin-top: 4px;
  margin-bottom: 8px;
}
.dialogueoption {
  font-size: 110%;
  margin-top: 4px;
  margin-bottom: 4px;
  margin-left: 12px;
}
.dialogueradiobutton {
  margin-right: 4px;
}
.orbitmessage {
  margin-top: 4px;
  font-size: 130%;
}
</style>
